// Reveals items in chunks as a sentinel scrolls into view. Cheap alternative
// to a virtualized list — fine for hundreds of rows, not for tens of
// thousands. Returns the visible slice plus a ref to attach to whatever
// element should trigger the next chunk.

import { useCallback, useEffect, useRef, useState } from "react";

interface Options {
  pageSize?: number;
  rootMargin?: string;
}

interface Result<T> {
  visible: T[];
  hasMore: boolean;
  sentinelRef: (node: HTMLElement | null) => void;
}

export function useInfiniteReveal<T>(items: T[], opts: Options = {}): Result<T> {
  const pageSize = opts.pageSize ?? 30;
  const rootMargin = opts.rootMargin ?? "200px";
  const [count, setCount] = useState(pageSize);
  const observerRef = useRef<IntersectionObserver | null>(null);

  // Reset visible count when the underlying list changes (e.g. search filter).
  useEffect(() => {
    setCount(pageSize);
  }, [items, pageSize]);

  const sentinelRef = useCallback(
    (node: HTMLElement | null) => {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
      if (!node) return;
      const observer = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          if (entry?.isIntersecting) {
            setCount((c) => c + pageSize);
          }
        },
        { rootMargin },
      );
      observer.observe(node);
      observerRef.current = observer;
    },
    [pageSize, rootMargin],
  );

  useEffect(() => () => observerRef.current?.disconnect(), []);

  const visible = items.slice(0, count);
  return { visible, hasMore: count < items.length, sentinelRef };
}
